import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography
} from '@mui/material';

import PrimaryButton from '../ui/buttons/primaryButton';
import { updatePlayerStatus } from '../../features/player/playerSlice';
import { setEvent } from '../../features/event/eventSlice';

export const PLAYER_STATUS = {
  ACTIVE: 'Active',
  WITHDRAWN: 'Withdrawn',
  DISQUALIFIED: 'Disqualified'
};

const PlayerStatusDialog = ({ open, onClose, player, eventId }) => {
  const dispatch = useDispatch();
  const updating = useSelector((state) => state.player.updatingPlayerStatus);
  const event = useSelector((state) => state.event.event);

  const [status, setStatus] = useState(PLAYER_STATUS.ACTIVE);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (player) {
      setStatus(player.status || PLAYER_STATUS.ACTIVE);
      setReason(player.statusReason || '');
      setError('');
    }
  }, [player]);

  const handleClose = () => {
    setError('');
    onClose();
  };

  const handleSubmit = async () => {
    if (status !== PLAYER_STATUS.ACTIVE && !reason.trim()) {
      setError('A reason is required.');
      return;
    }

    try {
      const result = await dispatch(
        updatePlayerStatus({ eventId, playerId: player.id, status, reason: reason.trim() })
      ).unwrap();
      if (result?.players && result?.teams && event) {
        dispatch(setEvent({ ...event, players: result.players, teams: result.teams }));
      }
      handleClose();
    } catch (err) {
      setError(err?.message || 'Failed to update player status.');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>Player Status</DialogTitle>
      <DialogContent>
        {player && (
          <Box display="flex" flexDirection="column" gap={2} sx={{ pt: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              {player.name} {player.surname}
            </Typography>

            <TextField
              select
              fullWidth
              label="Status"
              value={status}
              disabled={updating}
              onChange={(event) => setStatus(event.target.value)}>
              <MenuItem value={PLAYER_STATUS.ACTIVE}>Active</MenuItem>
              <MenuItem value={PLAYER_STATUS.WITHDRAWN}>Withdrawn</MenuItem>
              <MenuItem value={PLAYER_STATUS.DISQUALIFIED}>Disqualified</MenuItem>
            </TextField>

            {status !== PLAYER_STATUS.ACTIVE && (
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Reason*"
                value={reason}
                disabled={updating}
                onChange={(event) => setReason(event.target.value)}
              />
            )}

            {error && (
              <Typography variant="caption" color="error">
                {error}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={updating}>
          Cancel
        </Button>
        <PrimaryButton onClick={handleSubmit} variant="contained" disabled={updating}>
          Save Status
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};

export default PlayerStatusDialog;